"use client";

import Image from "next/image";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import HeroPlaceholder from "@/components/HomePage/HeroPlaceholder";
import { isUsableImageSrc, strapiImageUnoptimized } from "@/lib/strapi/strapiImage";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./Hero.scss";

const YOUTUBE_DESKTOP =
  "https://www.youtube-nocookie.com/embed/Fzn_Nm_ZfaE?autoplay=1&modestbranding=1&showinfo=0&rel=0&mute=1";
const YOUTUBE_MOBILE =
  "https://www.youtube-nocookie.com/embed/Fzn_Nm_ZfaE?autoplay=1&mute=1&showinfo=0&rel=0";

/** Intrinsic size hints for next/image — match layout aspect ratios (CSS controls display size). */
const BANNER1_W = 187;
const BANNER1_H = 158;
const BANNER23_W = 700;
const BANNER23_H = 289;

export type HeroStrapiBannerPair = {
  desktop: string;
  mobile: string;
};

type HeroStrapiClientProps = {
  first: HeroStrapiBannerPair;
  second: HeroStrapiBannerPair;
  third: HeroStrapiBannerPair;
};

function strapiUnopt(src: string) {
  return strapiImageUnoptimized(src);
}

export default function HeroStrapiClient({ first, second, third }: HeroStrapiClientProps) {
  const pairs = [first, second, third];
  const allValid = pairs.every(
    (p) => isUsableImageSrc(p.desktop) && isUsableImageSrc(p.mobile),
  );

  if (!allValid) {
    return <HeroPlaceholder />;
  }

  return (
    <div className="hero-section baseContainer">
      <div className="maxWidth hero-maxWidth">
        <div className="hero-container desktop">
          <Swiper
            autoHeight
            navigation
            pagination={{ type: "bullets", clickable: true }}
            modules={[Navigation, Pagination]}
          >
            <SwiperSlide>
              <div className="slide">
                <div className="left">
                  <Image
                    className="hero-next-image"
                    src={first.desktop}
                    width={BANNER1_W}
                    height={BANNER1_H}
                    alt="Premium Lumber"
                    priority
                    sizes="(min-width: 1101px) 48vw, 0"
                    unoptimized={strapiUnopt(first.desktop)}
                  />
                </div>

                <div className="right">
                  <div className="top">
                    <Image
                      className="hero-next-image replace-bg-none"
                      src={second.desktop}
                      width={BANNER23_W}
                      height={BANNER23_H}
                      alt="Feed"
                      priority
                      sizes="(min-width: 1101px) 48vw, 0"
                      unoptimized={strapiUnopt(second.desktop)}
                    />
                  </div>

                  <div className="bottom">
                    <Image
                      className="hero-next-image"
                      src={third.desktop}
                      width={BANNER23_W}
                      height={BANNER23_H}
                      alt="Paint, Stain & Colour Matching Services"
                      priority
                      sizes="(min-width: 1101px) 48vw, 0"
                      unoptimized={strapiUnopt(third.desktop)}
                    />
                  </div>
                </div>
              </div>
            </SwiperSlide>

            <SwiperSlide>
              <div className="slide fullSpace">
                <div className="left">
                  <iframe
                    width="1111"
                    height="625"
                    src={YOUTUBE_DESKTOP}
                    title="Grand Opening Ribbon Cutting June 3, 2022"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>

        <div className="hero-container mobile">
          <Swiper
            autoHeight
            observer
            observeSlideChildren
            navigation
            pagination={{ type: "bullets", clickable: true }}
            modules={[Navigation, Pagination]}
          >
            <SwiperSlide>
              <div className="slide">
                <div className="mobile-img mobile-img--ar-1">
                  <Image
                    className="hero-next-image"
                    src={first.mobile}
                    width={BANNER1_W}
                    height={BANNER1_H}
                    alt="Lumber"
                    priority
                    sizes="100vw"
                    unoptimized={strapiUnopt(first.mobile)}
                  />
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="slide">
                <div className="mobile-img mobile-img--ar-23">
                  <Image
                    className="hero-next-image"
                    src={second.mobile}
                    width={BANNER23_W}
                    height={BANNER23_H}
                    alt="Paint, Stain & Colour Matching Services"
                    sizes="100vw"
                    unoptimized={strapiUnopt(second.mobile)}
                  />
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="slide">
                <div className="mobile-img mobile-img--ar-23">
                  <Image
                    className="hero-next-image"
                    src={third.mobile}
                    width={BANNER23_W}
                    height={BANNER23_H}
                    alt="Feed"
                    sizes="100vw"
                    unoptimized={strapiUnopt(third.mobile)}
                  />
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="slide fullSpace">
                <div className="left">
                  <iframe
                    src={YOUTUBE_MOBILE}
                    title="Grand Opening Ribbon Cutting June 3, 2022"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
      </div>
    </div>
  );
}
